'use client';

import { useMemo, useState } from 'react';
import { SEOGEN_CONFIG, defaultSeoGenValues, type SeoGenVariant } from '@/lib/browser-seogen-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserSeoGenerator({ variant }: { variant: SeoGenVariant }) {
  const config = SEOGEN_CONFIG[variant];
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>(() => defaultSeoGenValues(variant));

  const output = useMemo(() => config.generate(values), [config, values]);

  function setValue(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{config.badge}</span>
        <h2 className="tool-live-title">{config.heading}</h2>
        <p className="tool-live-subtitle">{config.subtitle}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {config.fields.map((field) => {
              const className = field.full
                ? 'tool-live-field tool-live-field-full'
                : 'tool-live-field';
              return (
                <label className={className} key={field.key}>
                  <span>{field.label}</span>
                  {field.kind === 'textarea' ? (
                    <textarea
                      onChange={(e) => setValue(field.key, e.target.value)}
                      rows={field.rows ?? 3}
                      value={values[field.key] ?? ''}
                    />
                  ) : field.kind === 'select' ? (
                    <select
                      onChange={(e) => setValue(field.key, e.target.value)}
                      value={values[field.key] ?? ''}
                    >
                      {field.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      onChange={(e) => setValue(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      type="text"
                      value={values[field.key] ?? ''}
                    />
                  )}
                </label>
              );
            })}
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Output</span>
            <textarea readOnly rows={config.outputRows} value={output} />
          </label>
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
